import { useQuery } from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {useMemo} from "react";

export const GET_AUTOCOMPLETE = gql`
  query GetAutocomplete($query: String!) {
    getAutocomplete(query: $query) {
      brands {
        id
        name
        name_kr
      }
      search_recommendations {
        query
      }
    }
  }
`;

export const SEARCH_ITEM_TYPES = {
  HEADER: 'search_header',
  BRAND: 'search_brand',
  BRAND_MORE: 'search_brand_more',
  RECOMMENDATION: 'search_recommendation',
};
const MORE_BUTTON_STANDARD_COUNT = 3;

/**
 * useSearchAutoComplete Hook (Web)
 * @param {string} searchQuery - 사용자가 입력한 검색어
 * @return {object}
 */
export default function useSearchAutoComplete(searchQuery) {
  const { data, loading, error } = useQuery(GET_AUTOCOMPLETE, {
    variables: {
      query: searchQuery || '',
    },
    skip: !searchQuery,
    fetchPolicy: 'network-only',
  });

  // 검색 결과 데이터 가공
  const processedData = useMemo(() => {
    if (!searchQuery || loading) return null;
    if (error || !data || !data.getAutocomplete) return [];

    const getAutocomplete = data.getAutocomplete;
    const brands = getAutocomplete.brands || [];
    const search_recommendations = getAutocomplete.search_recommendations || [];
    const result = [];

    const hasTextData = search_recommendations.length > 0;
    const visibleBrands = hasTextData ? brands.slice(0, 3) : brands;

    visibleBrands.forEach((brand) => {
      result.push({
        id: `brand_${brand.id}`,
        type: SEARCH_ITEM_TYPES.BRAND,
        data: brand,
      });
    });

    if (hasTextData && brands.length > MORE_BUTTON_STANDARD_COUNT) {
      result.push({
        id: 'brand_more',
        type: SEARCH_ITEM_TYPES.BRAND_MORE,
        data: null,
      });
    }

    if (hasTextData) {
      result.push({
        id: 'recommendation_header',
        type: SEARCH_ITEM_TYPES.HEADER,
        data: { title: '추천 검색어' },
      });
    }

    search_recommendations.forEach((recommendation, index) => {
      result.push({
        id: `text_${recommendation.query}_${index}`,
        type: SEARCH_ITEM_TYPES.RECOMMENDATION,
        data: {
          text: recommendation.query,
        },
      });
    });

    return result;
  }, [searchQuery, data, loading, error]);

  // 더보기 클릭 시 사용할 추가 브랜드 데이터
  const moreData = useMemo(() => {
    if (loading || error || !data) return [];
    if (!data.getAutocomplete) return [];
    if (!data.getAutocomplete.brands) return [];

    const brands = data.getAutocomplete.brands;
    const hiddenBrands = brands.slice(3);

    return hiddenBrands.map((brand) => ({
      id: `brand_${brand.id}`,
      type: SEARCH_ITEM_TYPES.BRAND,
      data: brand,
    }));
  }, [data, loading, error]);

  // 더보기 클릭 시 기존 데이터에 추가 브랜드 데이터를 삽입하는 함수
  const insertMoreBrands = () => {
    if (!processedData) return [];
    const moreButtonIndex = processedData.findIndex(
      (item) => item.type === SEARCH_ITEM_TYPES.BRAND_MORE
    );
    if (moreButtonIndex === -1) return processedData;
    const newData = [...processedData];
    newData.splice(moreButtonIndex, 1, ...moreData);
    return newData;
  };

  return {
    data: processedData,
    insertMoreBrands,
    loading,
    error,
  };
}
